import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DEFAULT_ERROR_MODAL_TITLE,
  parseInviteUrlParams,
  parseRegistrationUrlParams,
  validateInviteEmailList,
} from "@buxus/shared";
import {
  registrationFlowContextFromUrl,
  trackRegistrationButtonPressed,
  trackRegistrationErrorOccurred,
  trackRegistrationFormSubmitted,
  trackRegistrationFormValidationFailed,
  trackRegistrationPageViewed,
} from "../platform/analytics/registrationAnalytics";
import { sendResponderInvites } from "./registrationApi";
import { friendlyRegistrationError, REGISTRATION_GENERIC_ERROR_MESSAGE } from "./registrationErrors";
import { leaveInviteHost, postDisplayErrorModal } from "./registrationHostBridge";
import {
  detectRegistrationHostKind,
  RegistrationShell,
  useRegistrationScrollToTop,
} from "./registrationShell";
import { reportRegistrationFailureIfServer } from "../platform/errors/webFailureReporting";
import "./InviteFlow.css";
import "./RegistrationFlow.css";

type InvitePhase = "form" | "success";

/** Commander invite flow at `/user/invite` (legacy buxusweb + mobile WebView parity). */
export function InviteFlow() {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollToTop = useRegistrationScrollToTop();
  const hostKind = useMemo(() => detectRegistrationHostKind(), []);
  const regFlowCtx = useMemo(
    () => registrationFlowContextFromUrl(parseRegistrationUrlParams(""), hostKind === "webview"),
    [hostKind],
  );
  const { referrerContactId } = useMemo(
    () => parseInviteUrlParams(location.search),
    [location.search],
  );

  const [phase, setPhase] = useState<InvitePhase>("form");
  const [emailsText, setEmailsText] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sentCount, setSentCount] = useState(0);

  useEffect(() => {
    trackRegistrationPageViewed("Invite", regFlowCtx, { referrerContactId: referrerContactId || undefined });
  }, [regFlowCtx, referrerContactId]);

  const validation = useMemo(() => validateInviteEmailList(emailsText), [emailsText]);
  const canSubmit = validation.error === null && validation.emails.length > 0 && !submitting;

  const onLeave = () => leaveInviteHost(navigate);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const checked = validateInviteEmailList(emailsText);
    if (checked.error) {
      setFieldError(checked.error);
      trackRegistrationFormValidationFailed({
        formName: "InviteForm",
        errorFields: ["emails"],
        errorMessages: [checked.error],
        ...regFlowCtx,
        totalSteps: 1,
        hasQuickCode: false,
        hasReferrerContactId: !!referrerContactId,
      });
      return;
    }
    setFieldError(null);
    setSubmitting(true);
    trackRegistrationFormSubmitted("InviteForm", regFlowCtx, {
      emailCount: checked.emails.length,
      hasReferrerContactId: !!referrerContactId,
    });
    try {
      await sendResponderInvites({
        emails: checked.emails,
        commanderId: referrerContactId,
      });
      setSentCount(checked.emails.length);
      setPhase("success");
      scrollToTop();
    } catch (err) {
      reportRegistrationFailureIfServer(err, "inviteSend");
      const detail = friendlyRegistrationError(err);
      trackRegistrationErrorOccurred("invite_send_failed", regFlowCtx, {
        emailCount: checked.emails.length,
        referrerContactId: referrerContactId || undefined,
        errorMessage: detail,
      });
      const fallback = `An error occurred while trying to send your ${
        checked.emails.length > 1 ? "invitations" : "invitation"
      }. Please try again.`;
      postDisplayErrorModal(
        detail === REGISTRATION_GENERIC_ERROR_MESSAGE ? fallback : detail,
        DEFAULT_ERROR_MODAL_TITLE,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const successCopy =
    sentCount > 1
      ? `Your ${sentCount} invitations have been sent.`
      : "Your invitation has been sent.";

  return (
    <RegistrationShell hostKind={hostKind} title="Invite Others">
      <div className="invite-flow">
        {phase === "success" ? (
          <section className="reg-card invite-card">
            <h2 className="reg-step-title">Sent</h2>
            <p className="reg-step-lead">{successCopy}</p>
            <div className="reg-actions reg-actions-intro">
              <button type="button" className="reg-btn primary" onClick={onLeave}>
                OK
              </button>
            </div>
          </section>
        ) : (
          <section className="reg-card invite-card">
            <h2 className="reg-step-title">Invite Others</h2>
            <form className="invite-form" onSubmit={(e) => void onSubmit(e)} noValidate>
              <p className="reg-step-lead">
                Want others from your department or agency to have access to information in Buxus?
              </p>
              <label className="reg-field invite-field">
                <span className="invite-label">Email addresses</span>
                <textarea
                  className={fieldError ? "reg-control--error" : undefined}
                  rows={6}
                  value={emailsText}
                  onChange={(e) => {
                    setEmailsText(e.target.value);
                    if (fieldError) setFieldError(null);
                  }}
                  onBlur={() => {
                    const next = validateInviteEmailList(emailsText);
                    setFieldError(next.error);
                  }}
                  placeholder="Enter email addresses"
                  autoCapitalize="none"
                  autoComplete="off"
                  spellCheck={false}
                  aria-invalid={fieldError ? true : undefined}
                  aria-describedby={fieldError ? "invite-email-error" : undefined}
                />
                {fieldError ? (
                  <small id="invite-email-error" className="reg-field-error">
                    {fieldError}
                  </small>
                ) : null}
              </label>
              <p className="invite-copy">
                Type or paste a list of <strong>comma-separated email addresses</strong> above and we
                will send them invitations to join Buxus.
              </p>
              <p className="invite-copy">
                These responders will be associated with your agency and station as you specified in
                your Buxus account.
              </p>
              <div className="reg-actions reg-actions-intro">
                <button
                  type="button"
                  className="reg-btn subtle"
                  onClick={() => {
                    trackRegistrationButtonPressed("Cancel", regFlowCtx, { screen: "Invite" });
                    onLeave();
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="reg-btn primary" disabled={!canSubmit}>
                  {submitting ? "Sending…" : "Send Invitations"}
                </button>
              </div>
            </form>
          </section>
        )}
      </div>
    </RegistrationShell>
  );
}
